import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  BarChart3, PieChart, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Clock, Building, GraduationCap, Brain, Activity, Plus, Settings, Save,
  Trash2, GripVertical, Maximize2, Minimize2, RefreshCw, Loader2,
  AlertCircle, Target, Calendar, Users, Shield, Gauge, ArrowUp, ArrowDown
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart as RechartsPie, Pie, Cell, Area, AreaChart, RadialBarChart, RadialBar
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Color constants
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const RISK_COLORS = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444'
};

// ============ Widget Components ============

const IncidentFrequencyWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/api/analytics/incident-trends`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: config?.days || 90 }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch incident trends:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [config?.days]);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  const chartData = data.weekly_incidents?.slice(-12) || [];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-2xl font-bold">{data.total_incidents}</p>
          <p className="text-sm text-gray-500">Total Incidents</p>
        </div>
        <div className={`flex items-center gap-1 ${data.trend_direction === 'increasing' ? 'text-red-500' : 'text-green-500'}`}>
          {data.trend_direction === 'increasing' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
          <span className="text-sm font-medium">{Math.abs(data.trend_percentage)}%</span>
        </div>
      </div>
      <div className="flex-1 min-h-[200px] min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const RiskGaugeWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/api/analytics/risk-score`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch risk score:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  const gaugeData = [{ name: 'Risk', value: data.risk_score, fill: RISK_COLORS[data.risk_level] }];

  return (
    <div className="h-full flex flex-col items-center justify-center">
      <div className="relative w-40 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="60%"
            outerRadius="100%"
            barSize={15}
            data={gaugeData}
            startAngle={180}
            endAngle={0}
          >
            <RadialBar
              background
              dataKey="value"
              cornerRadius={10}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color: RISK_COLORS[data.risk_level] }}>
            {data.risk_score}
          </span>
          <span className="text-xs text-gray-500 uppercase">{data.risk_level} Risk</span>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs w-full">
        <div className="text-center p-2 bg-gray-50 rounded">
          <p className="font-medium">{data.factors?.recent_incidents || 0}</p>
          <p className="text-gray-500">Incidents</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <p className="font-medium">{data.factors?.training_completion || 0}%</p>
          <p className="text-gray-500">Training</p>
        </div>
      </div>
    </div>
  );
};

const ComplianceCountdownWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/api/analytics/compliance-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch compliance status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-red-50 rounded-lg">
          <p className="text-xl font-bold text-red-600">{data.overdue_count}</p>
          <p className="text-xs text-red-600">Overdue</p>
        </div>
        <div className="text-center p-2 bg-yellow-50 rounded-lg">
          <p className="text-xl font-bold text-yellow-600">{data.upcoming_count}</p>
          <p className="text-xs text-yellow-600">Upcoming</p>
        </div>
        <div className="text-center p-2 bg-green-50 rounded-lg">
          <p className="text-xl font-bold text-green-600">{data.completed_count}</p>
          <p className="text-xs text-green-600">Completed</p>
        </div>
      </div>
      <div className="flex-1 overflow-auto space-y-2">
        {data.overdue_items?.slice(0, 3).map((item, i) => (
          <div key={i} className="flex items-center gap-2 p-2 bg-red-50 rounded text-sm">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <span className="flex-1 truncate">{item.title}</span>
            <Badge variant="destructive" className="text-xs">{item.days_overdue}d overdue</Badge>
          </div>
        ))}
        {data.upcoming_items?.slice(0, 3).map((item, i) => (
          <div key={i} className="flex items-center gap-2 p-2 bg-yellow-50 rounded text-sm">
            <Clock className="w-4 h-4 text-yellow-500 shrink-0" />
            <span className="flex-1 truncate">{item.title}</span>
            <Badge variant="outline" className="text-xs">{item.days_until}d left</Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrainingProgressWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/api/analytics/training-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch training progress:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  const pieData = [
    { name: 'Completed', value: data.completed, color: '#10b981' },
    { name: 'In Progress', value: data.in_progress, color: '#3b82f6' },
    { name: 'Overdue', value: data.overdue, color: '#ef4444' }
  ].filter(d => d.value > 0);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-2xl font-bold">{data.completion_rate}%</p>
          <p className="text-sm text-gray-500">Completion Rate</p>
        </div>
        {data.overdue > 0 && (
          <Badge variant="destructive">{data.overdue} Overdue</Badge>
        )}
      </div>
      <Progress value={data.completion_rate} className="h-2 mb-4" />
      <div className="flex-1 min-h-[120px] min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPie>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={50}
              dataKey="value"
              label={({ name, value }) => `${name}: ${value}`}
              labelLine={false}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </RechartsPie>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const NearMissTrendsWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/api/analytics/near-miss-trends`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch near-miss trends:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  const categoryData = Object.entries(data.by_category || {}).map(([name, value]) => ({
    name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  })).sort((a, b) => b.value - a.value).slice(0, 6);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-2xl font-bold">{data.total_near_misses}</p>
          <p className="text-sm text-gray-500">Total Near-Misses</p>
        </div>
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          Heinrich Ratio: {data.total_near_misses > 0 ? Math.round(data.total_near_misses / Math.max(1, data.total_near_misses / 10)) : 0}:1
        </Badge>
      </div>
      <div className="flex-1 min-h-[200px] min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={categoryData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 10 }} />
            <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DepartmentComparisonWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/api/analytics/department-comparison`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch department comparison:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  const chartData = data.departments?.slice(0, 8) || [];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{data.total_departments} Departments</p>
        {data.highest_risk && (
          <Badge variant="destructive">Highest Risk: {data.highest_risk}</Badge>
        )}
      </div>
      <div className="flex-1 min-h-[200px] min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="incidents" fill="#ef4444" name="Incidents" />
            <Bar dataKey="near_misses" fill="#f59e0b" name="Near Misses" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const AIPredictionsWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPredictions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/api/analytics/predict`, {
        days_to_analyze: config?.days || 90
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch predictions:', err);
      setError('Failed to generate predictions');
    } finally {
      setLoading(false);
    }
  }, [config?.days]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Brain className="w-8 h-8 text-purple-500 animate-pulse mb-2" />
        <p className="text-sm text-gray-500">AI analyzing safety data...</p>
      </div>
    );
  }

  if (error || !data?.risk_analysis) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <AlertCircle className="w-8 h-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">{error || 'No predictions available'}</p>
        <Button variant="outline" size="sm" onClick={fetchPredictions} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const analysis = data.risk_analysis;

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Risk Score Header */}
      <div className="flex items-center gap-4 mb-4 p-3 rounded-lg" style={{ backgroundColor: `${RISK_COLORS[analysis.risk_level]}15` }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: RISK_COLORS[analysis.risk_level] }}>
          <span className="text-white font-bold">{analysis.risk_score}</span>
        </div>
        <div className="flex-1">
          <p className="font-medium capitalize">{analysis.risk_level} Risk Level</p>
          <p className="text-xs text-gray-600">{analysis.executive_summary?.slice(0, 100)}...</p>
        </div>
      </div>

      {/* Predictions */}
      {analysis.predictions?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Predictions</p>
          {analysis.predictions.slice(0, 3).map((pred, i) => (
            <div key={i} className="p-2 bg-gray-50 rounded-lg text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">{pred.type}</span>
                <Badge variant="outline" style={{ 
                  backgroundColor: `${RISK_COLORS[pred.risk]}15`,
                  color: RISK_COLORS[pred.risk],
                  borderColor: RISK_COLORS[pred.risk]
                }}>
                  {pred.probability}% likely
                </Badge>
              </div>
              <p className="text-xs text-gray-600">{pred.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* High Risk Areas */}
      {analysis.high_risk_areas?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">High Risk Areas</p>
          <div className="flex flex-wrap gap-1">
            {analysis.high_risk_areas.map((area, i) => (
              <Badge key={i} variant="outline" className="bg-red-50 text-red-700 text-xs">
                {area}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const QuickStatsWidget = ({ config }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const [incidents, training, compliance] = await Promise.all([
          axios.get(`${API}/api/analytics/incident-trends`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/analytics/training-progress`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/analytics/compliance-status`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setData({
          incidents: incidents.data,
          training: training.data,
          compliance: compliance.data
        });
      } catch (error) {
        console.error('Failed to fetch quick stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <WidgetLoader />;
  if (!data) return <WidgetError message="Failed to load data" />;

  const stats = [
    {
      label: 'Total Incidents',
      value: data.incidents?.total_incidents || 0,
      icon: AlertTriangle,
      color: 'text-red-500',
      trend: data.incidents?.trend_direction
    },
    {
      label: 'Training Rate',
      value: `${data.training?.completion_rate || 0}%`,
      icon: GraduationCap,
      color: 'text-blue-500'
    },
    {
      label: 'Overdue Items',
      value: data.compliance?.overdue_count || 0,
      icon: Clock,
      color: 'text-yellow-500'
    },
    {
      label: 'Compliance Rate',
      value: `${data.compliance?.compliance_rate || 0}%`,
      icon: CheckCircle,
      color: 'text-green-500'
    }
  ];

  return (
    <div className="h-full grid grid-cols-4 gap-4">
      {stats.map((stat, i) => {
        const Icon = stat.icon;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gray-100 ${stat.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-bold">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============ Helper Components ============

const WidgetLoader = () => (
  <div className="h-full flex items-center justify-center">
    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
  </div>
);

const WidgetError = ({ message }) => (
  <div className="h-full flex flex-col items-center justify-center text-gray-400">
    <AlertCircle className="w-8 h-8 mb-2" />
    <p className="text-sm">{message}</p>
  </div>
);

// Widget type mapping
const WIDGET_COMPONENTS = {
  incident_frequency: IncidentFrequencyWidget,
  risk_gauge: RiskGaugeWidget,
  compliance_countdown: ComplianceCountdownWidget,
  training_progress: TrainingProgressWidget,
  near_miss_trends: NearMissTrendsWidget,
  department_comparison: DepartmentComparisonWidget,
  ai_predictions: AIPredictionsWidget,
  quick_stats: QuickStatsWidget
};

const WIDGET_ICONS = {
  incident_frequency: BarChart3,
  risk_gauge: Gauge,
  compliance_countdown: Clock,
  training_progress: GraduationCap,
  near_miss_trends: AlertTriangle,
  department_comparison: Building,
  ai_predictions: Brain,
  quick_stats: Activity
};

// ============ Main Dashboard Builder Component ============

const DashboardBuilder = () => {
  const { toast } = useToast();
  const [widgets, setWidgets] = useState([]);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [savedDashboards, setSavedDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [dashboardName, setDashboardName] = useState('My Dashboard');
  const [loading, setLoading] = useState(true);

  // Fetch available widget types and saved dashboards
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        const [widgetTypesRes, dashboardsRes] = await Promise.all([
          axios.get(`${API}/api/analytics/widget-types`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/analytics/dashboards`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        setAvailableWidgets(widgetTypesRes.data.widget_types || []);
        setSavedDashboards(dashboardsRes.data.dashboards || []);
        
        // Load default dashboard or create new one
        const defaultDashboard = dashboardsRes.data.dashboards?.find(d => d.is_default);
        if (defaultDashboard) {
          setWidgets(defaultDashboard.widgets || []);
          setCurrentDashboard(defaultDashboard);
          setDashboardName(defaultDashboard.name);
        } else {
          // Set default widgets
          setWidgets([
            { id: '1', type: 'quick_stats', title: 'Quick Stats', position: { x: 0, y: 0, w: 12, h: 2 } },
            { id: '2', type: 'risk_gauge', title: 'Risk Score', position: { x: 0, y: 2, w: 4, h: 4 } },
            { id: '3', type: 'incident_frequency', title: 'Incident Trends', position: { x: 4, y: 2, w: 8, h: 4 } },
            { id: '4', type: 'ai_predictions', title: 'AI Predictions', position: { x: 0, y: 6, w: 6, h: 5 } },
            { id: '5', type: 'department_comparison', title: 'Departments', position: { x: 6, y: 6, w: 6, h: 5 } }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const addWidget = (widgetType) => {
    const widgetInfo = availableWidgets.find(w => w.type === widgetType);
    const newWidget = {
      id: `widget-${Date.now()}`,
      type: widgetType,
      title: widgetInfo?.name || widgetType,
      position: {
        x: 0,
        y: Math.max(...widgets.map(w => (w.position?.y || 0) + (w.position?.h || 3)), 0),
        w: widgetInfo?.default_size?.w || 6,
        h: widgetInfo?.default_size?.h || 4
      },
      config: {}
    };
    setWidgets([...widgets, newWidget]);
    setShowAddWidget(false);
    toast({ title: 'Widget Added', description: `${widgetInfo?.name} added to dashboard` });
  };

  const removeWidget = (widgetId) => {
    setWidgets(widgets.filter(w => w.id !== widgetId));
    toast({ title: 'Widget Removed' });
  };

  const saveDashboard = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (currentDashboard) {
        await axios.put(`${API}/api/analytics/dashboards/${currentDashboard.id}`, {
          name: dashboardName,
          widgets: widgets,
          is_default: true
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const response = await axios.post(`${API}/api/analytics/dashboards`, {
          name: dashboardName,
          widgets: widgets,
          is_default: true
        }, { headers: { Authorization: `Bearer ${token}` } });
        setCurrentDashboard(response.data.dashboard);
      }
      
      toast({ title: 'Dashboard Saved', description: 'Your dashboard layout has been saved' });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save dashboard:', error);
      toast({ title: 'Error', description: 'Failed to save dashboard', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500">AI-powered safety insights and predictions</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Input
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                className="w-48"
                placeholder="Dashboard name"
              />
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button onClick={saveDashboard} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                <Settings className="w-4 h-4 mr-2" />
                Edit Dashboard
              </Button>
              <Dialog open={showAddWidget} onOpenChange={setShowAddWidget}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Widget
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Add Widget</DialogTitle>
                    <DialogDescription>Choose a widget to add to your dashboard</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {availableWidgets.map((widget) => {
                      const Icon = WIDGET_ICONS[widget.type] || Activity;
                      return (
                        <Card
                          key={widget.type}
                          className="cursor-pointer hover:border-blue-500 transition-colors"
                          onClick={() => addWidget(widget.type)}
                        >
                          <CardContent className="flex items-center gap-4 p-4">
                            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Icon className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{widget.name}</p>
                              <p className="text-sm text-gray-500">{widget.description}</p>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="grid grid-cols-12 gap-4">
        {widgets.map((widget) => {
          const WidgetComponent = WIDGET_COMPONENTS[widget.type];
          const Icon = WIDGET_ICONS[widget.type] || Activity;
          
          if (!WidgetComponent) return null;
          
          return (
            <Card
              key={widget.id}
              className="relative"
              style={{
                gridColumn: `span ${widget.position?.w || 6}`,
                minHeight: `${(widget.position?.h || 4) * 60}px`
              }}
              data-testid={`widget-${widget.type}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-500" />
                    {widget.title}
                  </CardTitle>
                  {isEditing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-gray-400 hover:text-red-500"
                      onClick={() => removeWidget(widget.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <WidgetComponent config={widget.config} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {widgets.length === 0 && (
        <Card className="py-12">
          <CardContent className="text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No widgets yet</h3>
            <p className="text-gray-500 mb-4">Add widgets to build your custom analytics dashboard</p>
            <Button onClick={() => setShowAddWidget(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Widget
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardBuilder;
